import { useEffect, useState } from "react";
import { Button, InputNumber, Segmented } from "antd";
import { CloseOutlined, EnvironmentOutlined } from "@ant-design/icons";
import {
  DEFAULT_GEO_DISPLAY_UNIT,
  displayUnitToKm,
  formatCoordinateLabel,
  kmToDisplayUnit,
} from "@/components/UI/TableToolbar/Filtering/geoFilterUtil";
import type {
  GeoDisplayUnit,
  GeoFilterCondition,
} from "@/components/UI/TableToolbar/Filtering/filterTypes";

const KM_COMPARISON_EPSILON = 0.001;

type RadiusEditorPanelProps = {
  condition: GeoFilterCondition;
  /** Draft = pin placed but the filter has not been applied yet. */
  isDraft: boolean;
  /** Draft edits are instant; applied edits are debounced by the caller. */
  onConditionChange: (condition: GeoFilterCondition) => void;
  onApply: () => void;
  onClose: () => void;
  onRemove: () => void;
};

export default function RadiusEditorPanel({
  condition,
  isDraft,
  onConditionChange,
  onApply,
  onClose,
  onRemove,
}: RadiusEditorPanelProps) {
  const [unit, setUnit] = useState<GeoDisplayUnit>(
    condition.display_unit ?? DEFAULT_GEO_DISPLAY_UNIT,
  );
  const [radius, setRadius] = useState<number | null>(
    kmToDisplayUnit(condition.radius_km, unit),
  );

  // Resync the inputs only when the condition changed externally (e.g. via
  // the chip popover) — our own edits round-trip back with the same value.
  useEffect(() => {
    const localKm = radius != null ? displayUnitToKm(radius, unit) : null;
    if (
      localKm == null ||
      Math.abs(localKm - condition.radius_km) > KM_COMPARISON_EPSILON
    ) {
      const nextUnit = condition.display_unit ?? DEFAULT_GEO_DISPLAY_UNIT;
      setUnit(nextUnit);
      setRadius(kmToDisplayUnit(condition.radius_km, nextUnit));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- external-change detector
  }, [condition.radius_km, condition.display_unit]);

  const handleRadiusChange = (value: number | null) => {
    setRadius(value);
    if (value != null && value > 0) {
      onConditionChange({
        ...condition,
        radius_km: displayUnitToKm(value, unit),
        display_unit: unit,
      });
    }
  };

  const handleUnitChange = (nextUnit: GeoDisplayUnit) => {
    setUnit(nextUnit);
    setRadius(kmToDisplayUnit(condition.radius_km, nextUnit));
    onConditionChange({ ...condition, display_unit: nextUnit });
  };

  const centerLabel =
    condition.label || formatCoordinateLabel(condition.lat, condition.lng);

  return (
    <div
      className="absolute top-2 left-2 z-[1000] w-[264px] rounded-lg bg-white p-3 shadow-lg border border-[#F0F2F4]"
      data-testid="radius-editor-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-bold text-[#2B4752]">
          Radius filter
        </span>
        <button
          type="button"
          aria-label="Close radius editor"
          className="text-[#8C8C8C] hover:text-[#2B4752]"
          onClick={onClose}
        >
          <CloseOutlined style={{ fontSize: "12px" }} />
        </button>
      </div>
      <div className="flex items-center gap-1.5 text-[12px] text-[#4B5563] mb-3">
        <EnvironmentOutlined />
        <span data-testid="radius-editor-center-label">{centerLabel}</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[13px] text-[#2B4752]">Within</span>
        <InputNumber
          size="small"
          min={0.1}
          value={radius}
          onChange={handleRadiusChange}
          className="w-[90px]"
          data-testid="radius-editor-radius-input"
        />
        <Segmented
          size="small"
          options={["mi", "km"]}
          value={unit}
          onChange={(value) => handleUnitChange(value as GeoDisplayUnit)}
          data-testid="radius-editor-unit-toggle"
        />
      </div>
      <p className="text-[11px] text-[#8C8C8C] mb-3">
        Drag the pin to move the center. Sites without coordinates are excluded
        from radius results.
      </p>
      <div className="flex justify-between border-t border-gray-200 pt-2">
        {isDraft ? (
          <>
            <Button type="text" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="small"
              className="bg-[#D2EFF9] border border-[#06709B] text-[#06709B]"
              onClick={onApply}
              data-testid="radius-editor-apply"
            >
              Apply filter
            </Button>
          </>
        ) : (
          <>
            <Button
              type="text"
              size="small"
              className="text-[#064C68]"
              onClick={onRemove}
              data-testid="radius-editor-remove"
            >
              Remove
            </Button>
            <Button size="small" onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
